import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Mail, ShieldCheck } from "lucide-react";

const inp = "w-full border-2 border-[#D3CFC0] bg-white px-2 py-1.5 text-sm focus:border-[#1E1E1E] outline-none";
const lab = "block text-[10px] uppercase font-[IBM_Plex_Mono] text-[#6E6E6E] mb-1";
const btn = "flex items-center gap-2 px-4 py-2 font-head uppercase text-sm border-2 transition-colors disabled:opacity-50";

function Field({ label, children }) {
  return <div><label className={lab}>{label}</label>{children}</div>;
}

export default function KitBuilder({ api, opts, initial }) {
  const [c, setC] = useState({ sport: initial?.sport || "Basketball", age: initial?.age || "7-9", practice_length: "",
    players_on_court: "", roster_size: "", practices_per_week: 2, team_name: "Sample Team", coach_first_name: "Coach",
    season_start: "", experience: "never", coaching_why: "" });
  const [email, setEmail] = useState(localStorage.getItem("sk_beta_email") || opts.owner_email || "");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const key = `${c.sport}|${c.age}`;
  const lengths = opts.lengths[key] || [];
  const oc = opts.on_court[key];

  useEffect(() => {
    setC((p) => ({ ...p, practice_length: lengths.includes(+p.practice_length) ? p.practice_length : lengths[lengths.length > 1 ? 1 : 0] || "",
      players_on_court: oc?.default || "", roster_size: opts.default_roster[key] || 10 }));
    setResult(null);
  }, [key]);

  const set = (k) => (e) => setC((p) => ({ ...p, [k]: e.target.value }));
  const body = () => ({ ...c, practice_length: +c.practice_length, players_on_court: +c.players_on_court || null,
    roster_size: +c.roster_size || null, practices_per_week: +c.practices_per_week, season_start: c.season_start || null });
  const fail = (e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Request failed");

  const validate = async () => {
    setBusy("validate");
    try { const { data } = await api.post("/admin/starter-kit/validate", body()); setResult(data); }
    catch (e) { fail(e); } finally { setBusy(""); }
  };
  const preview = async (kind) => {
    setBusy(kind);
    const w = window.open("", "_blank");
    try {
      const { data } = await api.post(`/admin/starter-kit/preview?kind=${kind}`, body(), { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) {
      if (w) w.close();
      let msg = "Preview failed";
      try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* keep default */ }
      toast.error(msg);
    } finally { setBusy(""); }
  };
  const beta = async () => {
    setBusy("beta");
    try {
      localStorage.setItem("sk_beta_email", email);
      const { data } = await api.post("/admin/starter-kit/beta", { ...body(), email });
      toast.success(data.email_status === "sent" ? `Beta kit emailed to ${email}` : `Beta kit created (email: ${data.email_status})`);
      setResult((r) => ({ ...(r || {}), beta: data }));
    } catch (e) { fail(e); } finally { setBusy(""); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-8" data-testid="sk-kit-builder">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sport"><select data-testid="sk-combo-sport" className={inp} value={c.sport} onChange={set("sport")}>{opts.sports.map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Age"><select data-testid="sk-combo-age" className={inp} value={c.age} onChange={set("age")}>{opts.ages.map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Practice length"><select data-testid="sk-combo-length" className={inp} value={c.practice_length} onChange={set("practice_length")}>{lengths.map((l) => <option key={l} value={l}>{`${l} min`}</option>)}</select></Field>
          <Field label="Players on court at once"><select data-testid="sk-combo-oncourt" className={inp} value={c.players_on_court} onChange={set("players_on_court")}>{(oc?.options || []).map((n) => <option key={n} value={n}>{`${n}${n === oc.default ? " (default)" : ""}`}</option>)}</select></Field>
          <Field label="Roster size"><input data-testid="sk-combo-roster" type="number" min={3} max={20} className={inp} value={c.roster_size} onChange={set("roster_size")} /></Field>
          <Field label="Practices / week"><input data-testid="sk-combo-ppw" type="number" min={1} max={5} className={inp} value={c.practices_per_week} onChange={set("practices_per_week")} /></Field>
          <Field label="Team name"><input data-testid="sk-combo-team" className={inp} value={c.team_name} onChange={set("team_name")} /></Field>
          <Field label="Coach first name"><input data-testid="sk-combo-coach" className={inp} value={c.coach_first_name} onChange={set("coach_first_name")} /></Field>
          <Field label="First practice (optional)"><input data-testid="sk-combo-season" type="date" className={inp} value={c.season_start} onChange={set("season_start")} /></Field>
          <Field label="Why I'm coaching (optional, 140)"><input data-testid="sk-combo-why" maxLength={140} className={inp} value={c.coaching_why} onChange={set("coaching_why")} /></Field>
          <Field label="Experience"><select data-testid="sk-combo-exp" className={inp} value={c.experience} onChange={set("experience")}><option value="never">Never coached</option><option value="some">Some experience</option></select></Field>
        </div>
        {!lengths.length && <p className="text-sm text-[#B3261E]">No lengths for this combination.</p>}
        <div className="flex flex-wrap gap-2 pt-2">
          <button data-testid="sk-validate-btn" disabled={!!busy || !lengths.length} onClick={validate} className={`${btn} border-[#1E1E1E] text-[#1E1E1E] hover:bg-[#F3F1EC]`}>{busy === "validate" ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Run validators</button>
          <button data-testid="sk-preview-guide-btn" disabled={!!busy || !lengths.length} onClick={() => preview("guide")} className={`${btn} border-[#1E1E1E] text-[#1E1E1E] hover:bg-[#F3F1EC]`}>{busy === "guide" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Preview guide</button>
          <button data-testid="sk-preview-cards-btn" disabled={!!busy || !lengths.length} onClick={() => preview("cards")} className={`${btn} border-[#1E1E1E] text-[#1E1E1E] hover:bg-[#F3F1EC]`}>{busy === "cards" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Preview cards</button>
        </div>
        <div className="border-2 border-[#1E1E1E] bg-[#1E1E1E] p-4 space-y-2">
          <p className="font-head uppercase text-[#F3F1EC]">Beta mode</p>
          <p className="text-xs text-[#C9C3B6]">Sends a free beta kit (guide + cards, small "BETA" footer tag, feedback link) and logs it as a beta order.</p>
          <div className="flex flex-wrap gap-2">
            <input data-testid="sk-beta-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="flex-1 min-w-[200px] border-2 border-[#3a3a3a] bg-[#111315] text-[#F3F1EC] px-2 py-1.5 text-sm outline-none focus:border-[#DB9130]" />
            <button data-testid="sk-beta-send-btn" disabled={!!busy || !email || !lengths.length} onClick={beta} className={`${btn} border-[#DB9130] bg-[#DB9130] text-[#1E1E1E] hover:bg-[#F3F1EC] hover:border-[#F3F1EC]`}>{busy === "beta" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email myself a beta kit</button>
          </div>
        </div>
      </div>
      <Results result={result} />
    </div>
  );
}

function Results({ result }) {
  if (!result) return <p className="text-sm text-[#6E6E6E]">Run the validators to see time-fit, rotation and age-rule checks for this combination.</p>;
  const errors = (result.checks || []).filter((x) => x.level === "error");
  const reviews = (result.checks || []).filter((x) => x.level === "review");
  return (
    <div className="space-y-4" data-testid="sk-results">
      {result.beta && (
        <div className="border-2 border-[#DB9130] p-3 text-sm" data-testid="sk-beta-result">
          <p className="font-head uppercase">Beta issued · email {result.beta.email_status}</p>
          <p className="text-xs break-all text-[#6E6E6E]">Feedback link: <a className="underline" href={result.beta.feedback_url} target="_blank" rel="noreferrer">{result.beta.feedback_url}</a></p>
        </div>
      )}
      {result.checks && (
        <>
          <p data-testid="sk-validate-summary" className={`font-head uppercase ${errors.length ? "text-[#B3261E]" : "text-[#1E1E1E]"}`}>
            {errors.length ? `${errors.length} blocking error(s)` : "All validators pass"} · {result.players_on_court} on court
          </p>
          {errors.map((e, i) => <p key={i} className="text-sm text-[#B3261E]">• {e.msg}</p>)}
          <p className="text-xs text-[#6E6E6E]">Coverage: {result.coverage.complete ? "complete (live-ready)" : `missing ${result.coverage.missing.join(", ")}`}</p>
          <p className="text-xs font-[IBM_Plex_Mono] text-[#6E6E6E]">Rotation periods per player: {result.rotation.join(" · ")}</p>
          {reviews.length > 0 && <div><p className="text-xs uppercase font-[IBM_Plex_Mono] text-[#DB9130]">Needs review ({reviews.length})</p>{reviews.map((e, i) => <p key={i} className="text-xs text-[#6E6E6E]">• {e.msg}</p>)}</div>}
          {result.plans.map((p) => (
            <div key={p.label} className="text-xs">
              <p className="font-head uppercase text-sm">{p.label}</p>
              {p.blocks.map(([n, m], i) => <span key={i} className="inline-block mr-1.5 mb-1 border border-[#D3CFC0] px-1.5">{n} {m}'</span>)}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
